import React, { useState } from 'react';
import { siteConfig } from '../data/seo';
import Footer from '../components/Footer';
import SmsConsentCheckbox from '../components/SmsConsentCheckbox';
import BookAppointmentLink from '../components/BookAppointmentLink';
import CherryFinancing from '../components/CherryFinancing';
import OpensInNewTab from '../components/OpensInNewTab';
import { saveContact, saveProspect } from '../services/database';
import { sendCustomerConfirmation, sendOwnerNotification } from '../services/email';
import { trackContactFormSubmit, trackEmailClick, trackPhoneClick } from '../utils/analytics';
import { log, logError } from '../utils/log';

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    petName: '',
    petType: '',
    concern: '',
    message: '',
    smsConsent: false
  });

  const [submissionState, setSubmissionState] = useState({
    isSubmitting: false,
    isSuccess: false,
    successMessage: 'Thank you! Your message has been sent.' as string,
    error: null as string | null
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmissionState({
      isSubmitting: true,
      isSuccess: false,
      successMessage: 'Thank you! Your message has been sent.',
      error: null,
    });
    
    try {
      const messageWithSmsConsent = [
        formData.message,
        `SMS consent checkbox: ${formData.smsConsent ? 'checked' : 'not checked'}.`,
      ].filter(Boolean).join('\n\n');

      // Save to Firestore
      const contactId = await saveContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: messageWithSmsConsent,
      });

      // Also save as prospect for marketing
      await saveProspect({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: messageWithSmsConsent,
      });

      // Send confirmation + notification emails. Their success/failure does
      // not block the form — the message is already saved in Firestore, so
      // the lead is captured either way. We just adjust the success copy if
      // mail delivery silently fails so the user (and us) know.
      const [customerOk, ownerOk] = await Promise.all([
        sendCustomerConfirmation({
          customerName: formData.name,
          customerEmail: formData.email,
          message: formData.message
        }),
        sendOwnerNotification({
          customerName: formData.name,
          customerEmail: formData.email,
          message: messageWithSmsConsent
        }),
      ]);

      const successMessage = customerOk && ownerOk
        ? 'Thank you! Your message has been sent.'
        : "Thanks — we received your message. (Our email confirmation system is having a hiccup, but your inquiry is logged and we'll be in touch shortly.)";

      if (!customerOk || !ownerOk) {
        logError('Email send issue — customerOk:', customerOk, 'ownerOk:', ownerOk);
      }

      setSubmissionState({ isSubmitting: false, isSuccess: true, successMessage, error: null });
      // Fires only after server-side ack (Firestore + email sends complete).
      // PII is SHA-256 hashed inside the helper before reaching dataLayer.
      void trackContactFormSubmit({
        email: formData.email,
        phone: formData.phone,
        firstName: formData.name,
        lastName: formData.lastName,
      });
      log('Contact saved successfully with ID:', contactId);
      
      // Reset form
      setFormData({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        petName: '',
        petType: '',
        concern: '',
        message: '',
        smsConsent: false
      });
      
    } catch (error) {
      logError('Error submitting contact:', error);
      setSubmissionState({
        isSubmitting: false,
        isSuccess: false,
        successMessage: 'Thank you! Your message has been sent.',
        error: 'Failed to submit message. Please try again or email us at info@chomp.vet.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-soft-gray to-white">
      {/* Header */}
      <section className="bg-brand-teal text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Contact Us
          </h1>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:info@chomp.vet"
              onClick={() => trackEmailClick('contact_page', 'info@chomp.vet')}
              className="bg-white text-brand-teal px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300"
            >
              <i className="fas fa-envelope mr-2"></i>
              Email info@chomp.vet
            </a>
            <BookAppointmentLink 
              analyticsSource="contact_page"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-brand-teal transition-all duration-300"
            >
              <i className="fas fa-calendar mr-2"></i>
              Book Now
            </BookAppointmentLink>
          </div>
        </div>
      </section>

      {/* Contact Form & Location */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Get in Touch
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Fill out the form below and one of our team members will get back to you as soon as possible.
              </p>

              {/* Feedback Messages */}
              {submissionState.isSuccess && (
                <div className="mb-6 p-4 rounded-lg bg-green-100 text-green-800">
                  {submissionState.successMessage}
                </div>
              )}
              {submissionState.error && (
                <div className="mb-6 p-4 rounded-lg bg-red-100 text-red-800">
                  {submissionState.error}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-first-name" className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      id="contact-first-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-gray-50"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-last-name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      id="contact-last-name"
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-gray-50"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-gray-50"
                    placeholder="Enter your email"
                  />
                </div>
                
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-gray-50"
                    placeholder="Enter your phone number"
                  />
                </div>

                <SmsConsentCheckbox
                  id="sms-consent"
                  checked={formData.smsConsent}
                  onChange={handleInputChange}
                />
                
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-gray-50"
                    placeholder="Enter your message"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submissionState.isSubmitting}
                  className={`w-full ${submissionState.isSubmitting ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'} text-white py-4 px-6 rounded-lg font-bold text-lg transition-all duration-300`}
                  aria-busy={submissionState.isSubmitting}
                >
                  {submissionState.isSubmitting ? 'SENDING…' : 'SEND MESSAGE'}
                </button>
              </form>
            </div>
            
            {/* Map */}
            <div>
              <div className="h-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3581.8736647482243!2d-80.11636222402393!3d26.19117377697088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9010fc2e58ba1%3A0x4e7faca3ba85e1f5!2s1800%20E%20Commercial%20Blvd%2C%20Fort%20Lauderdale%2C%20FL%2033308!5e0!3m2!1sen!2sus!4v1684234567890!5m2!1sen!2sus"
                  width="100%"
                  height="600"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-2xl shadow-lg"
                  title="CHOMP Pet Dental Location"
                ></iframe>
                
                <div className="mt-6 bg-card-bg rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-brand-teal rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-map-marker-alt text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-primary mb-2">Our Fort Lauderdale Clinic</h3>
                      <p className="text-gray-700">
                        1800 E Commercial Blvd<br/>
                        Fort Lauderdale, FL 33308
                      </p>
                      <p className="text-gray-700 mt-3"><strong>Hours:</strong> {siteConfig.openingHoursDisplay}</p>
                      <p className="text-gray-700 mt-3">Call <a className="text-brand-teal underline" href="tel:+19544663775" onClick={() => trackPhoneClick('contact_page')}>(954) 466-3775</a> for appointment availability.</p>
                      <a 
                        href="https://maps.google.com/?q=1800+E+Commercial+Blvd,+Fort+Lauderdale,+FL"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-teal font-semibold mt-2 hover:underline inline-block"
                      >
                        <i className="fas fa-directions mr-1" aria-hidden="true"></i>
                        Get Directions
                        <OpensInNewTab />
                      </a>
                    </div>
                  </div>
                </div>
                <CherryFinancing className="mt-6" compact />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ContactUs;